import { Box, Input, Paper, Typography } from '@mui/material'
import { StandardStateSelect } from '../../standards/StandardStateSelect'
import React, { useCallback } from 'react'
import { getGoogleDriveFolderId } from 'infrastructure/googleDrive/urlParser'
import { getSheet, googleUserAtom } from 'infrastructure/googleDrive'
import {
  addViaContentServiceFlow,
  importViaContentServiceFlow,
  parseFlowResults,
  transformApplicableStandardsForAddFlow,
} from './utilities'
import {
  StandardsImportApplicableStandards,
  StandardsImportErrors,
} from './types'
import { Table } from 'shared/ui/Table/Table'
import styled from '@emotion/styled'
import { StartAndMonitorFlow } from '../../contentService/FlowStartandMonitor'
import { ContentServiceFlowResults } from '../../contentService/types'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import { useGetTreeById } from 'shared/hooks/trees/hooks'

const SummaryBox = styled(Box)(() => ({
  padding: '16px',
  backgroundColor: '#f5f5f5',
  borderRadius: '4px',
  marginBottom: '16px',
}))

const SectionPaper = styled(Paper)(() => ({
  padding: '24px',
  marginBottom: '24px',
}))

const ScrollableTableContainer = styled(Box)(() => ({
  maxHeight: '400px',
  overflow: 'auto',
  border: '1px solid #e0e0e0',
  borderRadius: '4px',
}))

const issuesColumns = [
  {
    title: <strong>Node</strong>,
    width: 100,
    rowContent(a: StandardsImportErrors) {
      return (
        <a href={a.node_link} target="_blank" rel="noopener noreferrer">
          {a.node_title}
        </a>
      )
    },
  },
  {
    title: <strong>Issues</strong>,
    width: 300,
    rowContent(a: StandardsImportErrors) {
      return (
        <ul>
          {a.skills_lists.map((skillsList) => (
            <li key={skillsList.skills_title}>
              <strong>{skillsList.skills_title}</strong>
              <ul>
                {skillsList.duplicate.length > 0 && (
                  <li>
                    Duplicate skills:
                    <ul>
                      {skillsList.duplicate.map((skill) => (
                        <li key={skill}>{skill}</li>
                      ))}
                    </ul>
                  </li>
                )}
                {skillsList.not_found.length > 0 && (
                  <li>
                    Not found in Kiddom Database:
                    <ul>
                      {skillsList.not_found.map((skill) => (
                        <li key={skill}>{skill}</li>
                      ))}
                    </ul>
                  </li>
                )}
              </ul>
            </li>
          ))}
        </ul>
      )
    },
  },
]

const infoColumns = [
  {
    title: <strong>Node</strong>,
    width: 100,
    rowContent(a: StandardsImportApplicableStandards) {
      return (
        <a href={a.node_link} target="_blank" rel="noreferrer">
          {a.node_title}
        </a>
      )
    },
  },
  {
    title: <strong>Standards To Be Added</strong>,
    width: 100,
    rowContent(a: StandardsImportApplicableStandards) {
      return (
        <ul>
          {a.skills_lists.map((sl) => (
            <li key={sl.skills_title}>
              <strong>{sl.skills_title}</strong>
              <ul>
                {sl.standards.map((s) => (
                  <li key={s.standard_id}>{s.standard_name}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )
    },
  },
]

export function ImportStandards({
  treeId,
  treeVer,
}: {
  treeId: string
  treeVer: string
}) {
  const { tree } = useGetTreeById({ treeId, versionId: treeVer })
  const guser = useAtomValue(googleUserAtom)
  const [standardState, setStandardState] = React.useState<string>('')
  const [fileId, setFileId] = React.useState<string>()
  const [state, setState] = React.useState<
    | 'idle'
    | 'loading_file'
    | 'file_loaded'
    | 'input_started'
    | 'input_completed'
    | 'adding_started'
    | 'adding_completed'
  >('idle')

  const [csv, setCsv] = React.useState<string>()
  const [errors, setErrors] = React.useState<StandardsImportErrors[]>([])
  const [applicableStandards, setApplicableStandards] = React.useState<
    StandardsImportApplicableStandards[]
  >([])
  const uniqueStandardsNotFound = Array.from(
    new Set(
      errors.flatMap((i) => i.skills_lists.flatMap((sl) => sl.not_found)),
    ),
  )
  const uniqueStandards = Array.from(
    new Set(
      applicableStandards.flatMap((as) =>
        as.skills_lists.map((sl) => sl.standards),
      ),
    ),
  )

  const totalSkillLists = applicableStandards.reduce(
    (acc, ns) => acc + ns.skills_lists.length,
    0,
  )

  const totalStandards = applicableStandards.reduce(
    (acc, ns) =>
      acc +
      ns.skills_lists.reduce(
        (innerAcc, sl) => innerAcc + sl.standards.length,
        0,
      ),
    0,
  )

  const totalNodes = applicableStandards.length

  // Define all callbacks at the top level to avoid hook order issues
  const handleParseComplete = useCallback(
    (flowResults: ContentServiceFlowResults) => {
      // Parse flow results using the new parsing function
      const { applicableStandards, errors } = parseFlowResults(flowResults)

      setApplicableStandards(applicableStandards)
      setErrors(errors)

      // Set status to input_completed when import finishes
      setState('input_completed')
    },
    [],
  )

  const handleParseStart = useCallback(async () => {
    // Reset later components when starting new parse
    setState('input_started')
    setApplicableStandards([])
    setErrors([])

    return importViaContentServiceFlow({
      treeId: treeId,
      version: treeVer,
      content: btoa(unescape(encodeURIComponent(csv || ''))),
      standardState: standardState,
    })
  }, [treeId, treeVer, csv, standardState])

  const handleAddComplete = useCallback(() => {
    // Flow completed successfully
    setState('adding_completed')
  }, [])

  const handleAddStart = useCallback(async () => {
    // Set state to adding_started when starting add flow
    setState('adding_started')

    // Transform the applicable standards data to the format expected by add flow
    const transformedApplicableStandards =
      transformApplicableStandardsForAddFlow(applicableStandards)

    return addViaContentServiceFlow({
      tree_id: treeId,
      version_id: treeVer,
      node_ids: transformedApplicableStandards,
    })
  }, [treeId, treeVer, applicableStandards])

  React.useEffect(() => {
    if (!fileId) return
    setState('loading_file') // Reset state when new file ID is entered
    const googleDriveFileId = getGoogleDriveFolderId(fileId)
    getSheet(guser.credential?.accessToken || '', googleDriveFileId).then(
      (s) => {
        setCsv(s)
        setState('file_loaded')
      },
    )
  }, [fileId, guser.credential?.accessToken])

  if (!tree) {
    return <Box>Loading tree...</Box>
  }

  return (
    <Box>
      <SectionPaper>
        <Typography variant="h5" gutterBottom>
          Import Standards
        </Typography>
        <Box mb={2}>
          <StandardStateSelect
            value={standardState}
            onChange={(ss) => {
              setStandardState(ss)
            }}
          />
        </Box>
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom>
            Google Sheet URL
          </Typography>
          <Input
            placeholder="Google Drive File Id"
            value={fileId}
            onChange={(e) => setFileId(e.target.value)}
            fullWidth
          />
          {state === 'loading_file' && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Loading File...
            </Typography>
          )}
          {state === 'file_loaded' && csv && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Number of Rows: {csv.split('\n').length}
            </Typography>
          )}
        </Box>
        <StartAndMonitorFlow
          disabled={!csv || !standardState || state !== 'file_loaded'}
          buttonText="Parse via Content Service"
          processingText="Parsing via Content Service..."
          onFlowComplete={handleParseComplete}
          startFlowFunction={handleParseStart}
        />
      </SectionPaper>

      {/* Update Standards */}
      {applicableStandards.length > 0 && (
        <SectionPaper>
          <Typography variant="h5" gutterBottom>
            Update Standards
          </Typography>
          <SummaryBox>
            <Typography variant="body1">
              <strong>{totalStandards} standards</strong> across{' '}
              {totalSkillLists} skills lists in {totalNodes} nodes
            </Typography>
          </SummaryBox>
          <SummaryBox>
            <Typography variant="body1">
              <strong>{uniqueStandards.length} unique standards</strong>
            </Typography>
          </SummaryBox>
          {uniqueStandardsNotFound.length > 0 && (
            <SummaryBox>
              <Typography variant="body1" color="warning.main">
                <strong>
                  {uniqueStandardsNotFound.length} standards not found
                </strong>
              </Typography>
              <Box component="ul" mt={1}>
                {uniqueStandardsNotFound.map((s, index) => (
                  <li key={index}>{s}</li>
                ))}
              </Box>
            </SummaryBox>
          )}
          {errors.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle1" gutterBottom color="error">
                Errors ({errors.length})
              </Typography>
              <ScrollableTableContainer>
                <Table title="" columns={issuesColumns} items={errors} />
              </ScrollableTableContainer>
            </Box>
          )}
          <Box mt={2}>
            <ScrollableTableContainer>
              <Table
                title=""
                columns={infoColumns}
                items={applicableStandards}
              />
            </ScrollableTableContainer>
          </Box>
          <Box mt={2}>
            <StartAndMonitorFlow
              disabled={
                !applicableStandards.length || state !== 'input_completed'
              }
              buttonText="Add Standards via Content Service"
              processingText="Adding Standards via Content Service..."
              onFlowComplete={handleAddComplete}
              startFlowFunction={handleAddStart}
            />
          </Box>
        </SectionPaper>
      )}
    </Box>
  )
}
